import React from "react";

import { selectVersionConfig } from "fond/api";
import { CoreConfigurationForm, ScenarioConfigurationForm, SystemOfMeasurement } from "fond/types";
import { Currency } from "fond/types/currency";
import { useAppSelector } from "fond/utils/hooks";

import ConfigurationStep from "./ConfigurationStep";
import LayerSelectionStep from "./LayerSelectionStep";
import { Steps } from "./SetupModal";
import SpliceViewConfigurationStep from "./SpliceViewConfigurationStep";
import UploadConfigurationStep from "./UploadConfigurationStep";

export interface FormData extends Omit<ScenarioConfigurationForm, "Inputs"> {
  // Files uploaded by the user to the form, but not directly sent in the payload
  Files: {
    Occupancy: File | null;
    Artifacts: File[];
  };
  DefaultBins: boolean;
  SystemOfMeasurement: SystemOfMeasurement;
  Currency: Currency;
  // Sourced from / submitted to the version's ScenarioConfiguration.
  ScenarioConfigInput: ScenarioConfigurationForm["Inputs"];
  // Sourced from / submitted to the version's CoreConfiguration.
  CoreConfigInput: CoreConfigurationForm;
}

interface SetupFormProps {
  activeStep: Steps;
  isReadOnly?: boolean;
  hasExistingSpliceTables: boolean;
  isSpliceTablesLoading: boolean;
  isSpliceViewEnabled: boolean;
  onSubmit: React.SubmitEventHandler<HTMLFormElement>;
}

// Renders the setup form for the Collaboration CTS panel, including all configuration steps.
const SetupForm: React.FC<SetupFormProps> = ({
  activeStep,
  isReadOnly,
  hasExistingSpliceTables,
  isSpliceTablesLoading,
  isSpliceViewEnabled,
  onSubmit,
}) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));

  const renderStepContent = (step: Steps) => {
    switch (step) {
      case Steps.UPLOAD:
        return (
          <UploadConfigurationStep
            isReadOnly={isReadOnly}
            versionId={versionId}
            hasExistingSpliceTables={hasExistingSpliceTables}
            isSpliceTablesLoading={isSpliceTablesLoading}
            isSpliceViewEnabled={isSpliceViewEnabled}
          />
        );
      case Steps.LAYER_SELECTION:
        return <LayerSelectionStep config={config} isReadOnly={isReadOnly} versionId={versionId} />;
      case Steps.COST_TO_SERVE_CONFIGURATION:
        return <ConfigurationStep config={config} isReadOnly={isReadOnly} versionId={versionId} />;
      case Steps.SPLICE_VIEW_CONFIGURATION:
        return (
          <SpliceViewConfigurationStep
            isReadOnly={isReadOnly}
            hasExistingSpliceTables={hasExistingSpliceTables}
            isSpliceTablesLoading={isSpliceTablesLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <form id="collaboration-cts-form" onSubmit={onSubmit}>
      {renderStepContent(activeStep)}
    </form>
  );
};

export default SetupForm;
